// KPIs › Reports (NAV-REDESIGN; was the Reports mode): the Friday report list
// (`/c/{slug}/kpis/reports`) and one week (`/c/{slug}/kpis/reports/{week}`, the preview: S1-17
// screen 9; the old `/reports/…` paths redirect), both behind the `report-weeks` gate
// (`kpi`, company#reader); and (KPIS-MODE, 2026-10-22) Finance, c.$slug.reports.finance.tsx.
// REPORTS-UI: the report email's two links land here: a version by id (`/reports/{id}`, sent
// to its week) and `/reports/unsubscribe` (the caller's own report email, no mode gate: its
// screen says why when there is nothing to change).
import { EmptyState } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import {
  createRoute,
  Navigate,
  notFound,
  Outlet,
  redirect,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import type { Api } from "../api/client";
import { canOpen, modeByKey, openOnly } from "../extensions/modes";
import { useLiveCanvas } from "../features/canvas/live-channel";
import type { CompanyAccess } from "../features/company";
import { PortfolioLiveScreen } from "../features/portfolio/portfolio-live";
import { keys } from "../features/queries";
import { ReportUnsubscribe } from "../features/reports/report-email";
import { ReportPreview } from "../features/reports/report-preview";
import { ReportWeeks } from "../features/reports/report-weeks";
import { LoadError, Loading } from "../features/screen-parts";
import { useCompany, useSession } from "../features/use-feature";
import { keepFocus, reconcileHref } from "../focus/focus";
import { ModeGate } from "../shell/mode-gate";
import { kpisRoute } from "./c.$slug.kpis";

const WEEK = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;
/** REPORTS-UI: the report email links one version by id (`reportWebUrl`); it opens its week. */
const REPORT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** REPORTS-UI: the landing of the report email's "Stop getting this report" link. */
export const reportUnsubscribeRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: "unsubscribe",
  component: function ReportUnsubscribePage() {
    const { api } = reportUnsubscribeRoute.useRouteContext();
    return <ReportUnsubscribe api={api} company={useCompany()} />;
  },
});

/**
 * FOUNDER-OS phase 1 (design §3.1): Reports › Live, the live picture across the portfolio's
 * companies. Founder OS only; elsewhere the page says where it lives.
 */
export const reportLiveRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: "live",
  component: function ReportLive() {
    const { api } = reportLiveRoute.useRouteContext();
    const company = useCompany();
    // CANVAS-CORE: where the company's `live_canvas` switch is on (the read answers), the canvas
    // lives in the one Live page (CANVAS-BLOCKS): go there, on its Workspace. A 404 (switch off)
    // or any failure, and the in-memory fake (no canvas there), keep the page as it was.
    const live = useLiveCanvas(api, company.id);
    if (api.mode === "http") {
      if (live.state.isPending) return <Loading label="Opening Live" rows={3} />;
      if (live.state.data)
        return (
          <Navigate
            to="/c/$slug/live"
            params={{ slug: company.slug }}
            search={{ view: "workspace" } as never}
            replace
          />
        );
    }
    if (company.kind !== "portfolio")
      return (
        <EmptyState title="Live is in Founder OS" description="Open it from your Founder OS." />
      );
    return <PortfolioLiveScreen api={api} company={company} />;
  },
});

// NAV-REDESIGN (2026-10-29): Reports is a section of KPIs (`/kpis/reports/…`); the old
// `/reports/…` paths redirect (c.$slug.legacy.tsx).
export const reportsRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "reports",
  // Each page holds its own gate: the Friday reports `report-weeks`, Finance `finance` (a seat
  // holder reads its own budget by URL and from the chart without the Reports tab).
  component: Outlet,
});

export const reportsIndexRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: "/",
  component: function Reports() {
    const { api } = reportsIndexRoute.useRouteContext();
    const { me } = useSession();
    const company = useCompany();
    // FOUNDER-OS phase 1: Founder OS has no Friday reports; Reports opens on Live.
    if (company.kind === "portfolio")
      return (
        <Navigate
          to="/c/$slug/kpis/reports/live"
          params={{ slug: company.slug }}
          search={keepFocus as never}
          replace
        />
      );
    // KPIS-MODE: a member (Finance only) opens Reports on Finance.
    const reports = modeByKey("reports");
    if (reports && openOnly(reports, company) && canOpen("finance", me, company))
      return (
        <Navigate
          to="/c/$slug/kpis/reports/finance"
          params={{ slug: company.slug }}
          search={keepFocus as never}
          replace
        />
      );
    return (
      <ModeGate modeKey="report-weeks">
        <ReportWeeks api={api} company={company} />
      </ModeGate>
    );
  },
});

export const reportWeekRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: "$week",
  // The week in the path is the focused period; the query is reconciled in navigation, and
  // the week picker navigates to another week's path (see FocusProvider.setFocus).
  beforeLoad: ({ params, location }) => {
    if (REPORT_ID.test(params.week)) return;
    if (!WEEK.test(params.week)) throw notFound();
    const href = reconcileHref(location, { period: params.week });
    if (href) throw redirect({ href, replace: true });
  },
  component: function ReportWeek() {
    const { week } = reportWeekRoute.useParams();
    const { api } = reportWeekRoute.useRouteContext();
    const company = useCompany();
    // Keyed by week: another week starts with its own version choice and stale flags.
    return (
      <ModeGate modeKey="report-weeks">
        {REPORT_ID.test(week) ? (
          <ReportById api={api} company={company} id={week} />
        ) : (
          <ReportPreview key={week} api={api} company={company} week={week} />
        )}
      </ModeGate>
    );
  },
});

/**
 * A version's id (the email's link): its week's page showing that version (not the newest);
 * unknown, or unsent to a client viewer: the API's 404.
 */
function ReportById({ api, company, id }: { api: Api; company: CompanyAccess; id: string }) {
  const q = useQuery({
    // Under the reports prefix: send, resend, reconcile and live updates refresh it too.
    queryKey: [...keys.reports(company.id), "by-id", id],
    queryFn: () => api.client.reports.get({ params: { co: company.id, id } }),
    retry: false,
  });
  const location = useRouterState({ select: (s) => s.location });
  const router = useRouter();
  // The shell's focused week follows the version's week (the path keeps the id).
  const href = q.data ? reconcileHref(location, { period: q.data.week_key }) : null;
  useEffect(() => {
    if (href) router.history.replace(href);
  }, [href, router]);
  if (q.isPending) return <Loading label="Opening the report" rows={3} />;
  if (q.isError) return <LoadError what="The report" error={q.error} />;
  return (
    <ReportPreview key={id} api={api} company={company} week={q.data.week_key} pinned={q.data} />
  );
}
